import React from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, CircleDot, Circle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSetRecoilState } from "recoil";
import { brokerConnectModalAtom } from "@/Atoms/modal/DialogueAtoms";
import { useRouter } from "next/router";
import { useAppDetails } from "@/contexts/AppDetailsContext";

interface OnboardingBarProps {
  onboardingData?: {
    onboarding_completed?: boolean;
    onboarding_metrics?: {
      strategy_created?: boolean;
      backtest_completed?: boolean;
      broker_connected?: boolean;
      strategy_deployed?: boolean;
    };
  };
}

const OnboardingBar = ({ onboardingData }: OnboardingBarProps) => {
  const { t } = useTranslation("layout-page");
  const setBrokerModal = useSetRecoilState(brokerConnectModalAtom);
  const router = useRouter();
  const { details } = useAppDetails();
  if (!onboardingData) {
    return null;
  }

  // If onboarding is completed or not provided, we don't show the bar
  if (onboardingData.onboarding_completed !== false) {
    return null;
  }

  const metrics = onboardingData.onboarding_metrics || {};

  const steps = [
    {
      id: "create",
      label: t("Create Strategy"),
      completed: !!metrics.strategy_created,
    },
    {
      id: "backtest",
      label: t("Run Backtest"),
      completed: !!metrics.backtest_completed,
    },
    ...(!details?.isWhiteLabled
      ? [
          {
            id: "broker",
            label: t("Connect Broker"),
            completed: !!metrics.broker_connected,
          },
        ]
      : []),
    {
      id: "deploy",
      label: t("Deploy Strategy"),
      completed: !!metrics.strategy_deployed,
    },
  ];

  const handleStepClick = (id: string) => {
    if (id === "broker") {
      setBrokerModal({ isOpen: true });
    } else if (id === "deploy") {
      router.push("/test");
    }
  };

  return (
    <div className="flex  flex-row items-center gap-4 sm:gap-6 mx-auto w-full max-w-4xl justify-center px-2">
      <div className="flex flex-row items-center justify-between w-full md:w-auto md:grow gap-2 sm:gap-4 md:ml-4">
        {steps.map((step, index) => {
          const isPending =
            !step.completed && (index === 0 || steps[index - 1].completed);
          const isClickable = step.id === "broker" || step.id === "deploy";

          return (
            <React.Fragment key={index}>
              <div
                className={cn(
                  "flex flex-row items-center gap-2 min-w-max transition-all",
                  isClickable && "cursor-pointer hover:opacity-80",
                )}
                onClick={() => isClickable && handleStepClick(step.id)}
              >
                {step.completed ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : isPending ? (
                  <CircleDot className="w-4 h-4 text-blue-500 animate-pulse" />
                ) : (
                  <Circle className="w-4 h-4 text-muted-foreground/50" />
                )}
                <span
                  className={cn(
                    "font-medium whitespace-nowrap transition-colors duration-300",
                    step.completed
                      ? "text-white"
                      : isPending
                        ? "text-white"
                        : "text-muted-foreground/60",
                  )}
                >
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "h-[1px] flex-grow hidden sm:block mx-1 min-w-[20px] transition-colors duration-300",
                    step.completed && !isPending && steps[index + 1].completed
                      ? "bg-green-500/30"
                      : "bg-white/10",
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default OnboardingBar;
